import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  getTheme,
  Label,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text
} from "@fluentui/react";
import { useState } from "react";

import { ContractItem } from "../../contract.interface";
import { phoneNumberFormat } from "../../ProductColumns/helpers";

import { useApi } from "@/core/libs/api/react-query/useApi";

interface DetachInventoryModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  item?: ContractItem;
  onSuccess?: () => Promise<void>;
}

export function DetachInventoryModal({
  isOpen,
  onDismiss,
  item,
  onSuccess
}: DetachInventoryModalProps): JSX.Element {
  const theme = getTheme();
  const { execute } = useApi();

  const inventory = item?.inventory?.[0];
  const product = inventory?.product;
  const distributionCenter = inventory?.distributionCenter;
  const lineNumber = item?.line?.number
    ? phoneNumberFormat(String(item.line.number))
    : "-";

  const isConfirmDisabled = !inventory;

  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    if (isLoading) return;
    onDismiss();
  };

  const handleConfirm = async () => {
    if (!item) return;

    try {
      setIsLoading(true);

      await execute({
        url: `contracts/items/${item.id}/detach-inventory`,
        method: "DELETE",
        showAlert: true,
        messages: {
          success: "Inventário desvinculado com sucesso!",
          error: "Erro ao desvincular inventário."
        }
      });

      if (onSuccess) await onSuccess();

      onDismiss();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleClose}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Desvincular Inventário"
      }}
      minWidth={500}
    >
      <Stack tokens={{ childrenGap: 12 }}>
        <Text>
          Confirme o desvínculo do inventário abaixo da linha selecionada.
        </Text>

        <section
          aria-labelledby="detach-inventory-details-title"
          style={{
            backgroundColor: theme.palette.neutralLighter,
            borderRadius: 4,
            padding: 16
          }}
        >
          <Text
            id="detach-inventory-details-title"
            as="h3"
            variant="mediumPlus"
            styles={{ root: { fontWeight: 600 } }}
          >
            Dados para confirmação
          </Text>

          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <Label styles={{ root: { fontWeight: 600 } }}>Linha:</Label>
            <Text>{lineNumber}</Text>
          </Stack>

          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <Label styles={{ root: { fontWeight: 600 } }}>
              Número de Série:
            </Label>
            <Text>{inventory?.serialNumber}</Text>
          </Stack>

          {product && (
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 4 }}
            >
              <Label styles={{ root: { fontWeight: 600 } }}>Produto:</Label>
              <Text>{product.name}</Text>
            </Stack>
          )}

          {distributionCenter && (
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 4 }}
            >
              <Label styles={{ root: { fontWeight: 600 } }}>
                Centro de Distribuição:
              </Label>
              <Text>{distributionCenter.name}</Text>
            </Stack>
          )}
        </section>

        {isLoading && (
          <Stack
            as="output"
            horizontalAlign="center"
            tokens={{ padding: 16 }}
            aria-live="polite"
          >
            <Spinner size={SpinnerSize.medium} label="Carregando..." />
          </Stack>
        )}
      </Stack>

      <DialogFooter>
        <DefaultButton
          text="Cancelar"
          onClick={handleClose}
          disabled={isLoading}
        />
        <PrimaryButton
          text="Confirmar"
          onClick={() => void handleConfirm()}
          disabled={isConfirmDisabled || isLoading}
        />
      </DialogFooter>
    </Dialog>
  );
}

export default DetachInventoryModal;
